// src/pages/BookingsAnalyticsPage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  Hotel,
  EventAvailable,
  Percent,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  RadialBarChart,
  RadialBar,
  BarChart,
  Line,
} from 'recharts';
import { api } from '../services/api';

export interface OccupancyDataPoint {
  date: string;
  label: string;
  bookingCount: number;
  checkIns: number;
  checkOuts: number;
  occupancyPct: number;
  // for YoY: prior year value mapped onto same x-axis position
  priorYearCount?: number;
}

export interface MonthlyDataPoint {
  month: string;
  checkIns: number;
  revenue: number;
}

interface OccupancyResponse {
  totalKennels: number;
  data: {
    date: string;
    bookingCount: number;
    checkIns: number;
    checkOuts: number;
    occupancyPct: number;
  }[];
}

export type DateRange = '30days' | '90days' | 'thisYear' | 'lastYear' | 'yoy';

const RANGE_LABELS: Record<DateRange, string> = {
  '30days': 'Last 30 days',
  '90days': 'Last 90 days',
  thisYear: 'This year',
  lastYear: 'Last year',
  yoy: 'Year over year',
};

export function getRangeDates(range: DateRange): {
  startDate: string;
  endDate: string;
  priorStartDate?: string;
  priorEndDate?: string;
} {
  const today = new Date();
  const toStr = (d: Date) => d.toISOString().split('T')[0];

  if (range === '30days') {
    const start = new Date(today);
    start.setDate(today.getDate() - 29);
    return { startDate: toStr(start), endDate: toStr(today) };
  }
  if (range === '90days') {
    const start = new Date(today);
    start.setDate(today.getDate() - 89);
    return { startDate: toStr(start), endDate: toStr(today) };
  }
  if (range === 'thisYear') {
    return {
      startDate: `${today.getFullYear()}-01-01`,
      endDate: toStr(today),
    };
  }
  if (range === 'lastYear') {
    const y = today.getFullYear() - 1;
    return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
  }
  // YoY: this year + last year for overlay
  if (range === 'yoy') {
    return {
      startDate: `${today.getFullYear()}-01-01`,
      endDate: toStr(today),
      priorStartDate: `${today.getFullYear() - 1}-01-01`,
      priorEndDate: `${today.getFullYear() - 1}-12-31`,
    };
  }
  return { startDate: toStr(today), endDate: toStr(today) };
}

export function formatDateLabel(dateStr: string, range: DateRange): string {
  const d = new Date(dateStr + 'T00:00:00');
  if (range === '30days' || range === '90days') {
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  }
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

export function buildMonthlyBars(
  data: OccupancyDataPoint[]
): MonthlyDataPoint[] {
  const map = new Map<string, { checkIns: number; revenue: number }>();
  for (const d of data) {
    const month = d.date.substring(0, 7); // YYYY-MM
    const existing = map.get(month) ?? { checkIns: 0, revenue: 0 };
    existing.checkIns += d.checkIns;
    map.set(month, existing);
  }
  return Array.from(map.entries()).map(([month, v]) => ({
    month: new Date(month + '-01').toLocaleDateString('en-ZA', {
      month: 'short',
      year: '2-digit',
    }),
    checkIns: v.checkIns,
    revenue: v.revenue,
  }));
}

const BookingsAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [range, setRange] = useState<DateRange>('30days');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OccupancyDataPoint[]>([]);
  const [priorData, setPriorData] = useState<OccupancyDataPoint[]>([]);
  const [totalKennels, setTotalKennels] = useState(0);

  const fetchData = useCallback(async (selectedRange: DateRange) => {
    setLoading(true);
    try {
      const { startDate, endDate, priorStartDate, priorEndDate } =
        getRangeDates(selectedRange);

      const res = await api.get<OccupancyResponse>('/Booking/occupancy', {
        params: { startDate, endDate },
      });

      const mapped: OccupancyDataPoint[] = res.data.data.map((d) => ({
        date: d.date,
        label: formatDateLabel(d.date, selectedRange),
        bookingCount: d.bookingCount,
        checkIns: d.checkIns,
        checkOuts: d.checkOuts,
        occupancyPct: d.occupancyPct,
      }));

      setTotalKennels(res.data.totalKennels);
      setData(mapped);

      if (selectedRange === 'yoy' && priorStartDate && priorEndDate) {
        const priorRes = await api.get<OccupancyResponse>(
          '/Booking/occupancy',
          {
            params: { startDate: priorStartDate, endDate: priorEndDate },
          }
        );
        const priorMapped: OccupancyDataPoint[] = priorRes.data.data.map(
          (d) => ({
            date: d.date,
            label: formatDateLabel(d.date, selectedRange),
            bookingCount: d.bookingCount,
            checkIns: d.checkIns,
            checkOuts: d.checkOuts,
            occupancyPct: d.occupancyPct,
          })
        );
        setPriorData(priorMapped);
      } else {
        setPriorData([]);
      }
    } catch {
      // errors handled by interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(range);
  }, [range, fetchData]);

  // Merge YoY data: align by day-of-year index
  const chartData: OccupancyDataPoint[] = React.useMemo(() => {
    if (range !== 'yoy' || priorData.length === 0) return data;
    return data.map((d, i) => ({
      ...d,
      priorYearCount: priorData[i]?.bookingCount ?? undefined,
    }));
  }, [data, priorData, range]);

  const monthlyData = React.useMemo(() => buildMonthlyBars(data), [data]);

  // Summary stats
  const avgOccupancy =
    data.length > 0
      ? Math.round(data.reduce((s, d) => s + d.occupancyPct, 0) / data.length)
      : 0;
  const peakBookings =
    data.length > 0 ? Math.max(...data.map((d) => d.bookingCount)) : 0;
  const totalCheckIns = data.reduce((s, d) => s + d.checkIns, 0);
  const currentOccupancy =
    data.length > 0 ? (data[data.length - 1]?.occupancyPct ?? 0) : 0;

  const radialData = [
    {
      name: 'Occupancy',
      value: currentOccupancy,
      fill:
        currentOccupancy >= 80
          ? theme.palette.error.main
          : currentOccupancy >= 50
            ? theme.palette.warning.main
            : theme.palette.success.main,
    },
  ];

  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;
  const successColor = theme.palette.success.main;
  const isDark = theme.palette.mode === 'dark';

  const gridColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const axisColor = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)';

  // Thin the x-axis tick labels for smaller screens / large datasets
  const tickInterval =
    chartData.length > 60
      ? Math.floor(chartData.length / 12)
      : chartData.length > 20
        ? 3
        : 1;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          variant="text"
          size="small"
        >
          Back
        </Button>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => fetchData(range)}
          disabled={loading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 700 }}>
        Bookings Analytics
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 3 }}>
        Kennel occupancy, check-in volume, and booking trends over time.
      </Typography>

      {/* Range selector */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={(_, v) => {
            if (v) setRange(v as DateRange);
          }}
          size="small"
          sx={{ flexWrap: 'wrap' }}
        >
          {(Object.keys(RANGE_LABELS) as DateRange[]).map((key) => (
            <ToggleButton key={key} value={key}>
              {RANGE_LABELS[key]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {loading && (
          <CircularProgress size={18} sx={{ ml: 2, verticalAlign: 'middle' }} />
        )}
      </Box>

      {/* Summary stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Current occupancy
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {currentOccupancy}%
                  </Typography>
                </Box>
                <Percent
                  sx={{ fontSize: 36, color: 'primary.main', opacity: 0.7 }}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                {Math.round((currentOccupancy / 100) * totalKennels)} of{' '}
                {totalKennels} kennels
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Avg occupancy
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {avgOccupancy}%
                  </Typography>
                </Box>
                <TrendingUp
                  sx={{ fontSize: 36, color: 'success.main', opacity: 0.7 }}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Over selected period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Peak bookings
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {peakBookings}
                  </Typography>
                </Box>
                <Hotel
                  sx={{ fontSize: 36, color: 'secondary.main', opacity: 0.7 }}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Highest single-day count
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total check-ins
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {totalCheckIns}
                  </Typography>
                </Box>
                <EventAvailable
                  sx={{ fontSize: 36, color: 'warning.main', opacity: 0.7 }}
                />
              </Box>
              <Typography variant="caption" color="textSecondary">
                Over selected period
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Hero chart: occupancy over time */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Kennel occupancy over time
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Active bookings per day.
          {range === 'yoy' && ' Ghost line shows the same period last year.'}
          {totalKennels > 0 && ` Capacity: ${totalKennels} kennels.`}
        </Typography>

        {chartData.length === 0 && !loading ? (
          <Typography color="textSecondary">
            No data available for this period.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={isMobile ? 240 : 340}>
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: isMobile ? 0 : 40, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="occupancyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={primaryColor}
                    stopOpacity={0.35}
                  />
                  <stop
                    offset="95%"
                    stopColor={primaryColor}
                    stopOpacity={0.03}
                  />
                </linearGradient>
                <linearGradient id="priorGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={axisColor} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={axisColor} stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={gridColor}
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: axisColor }}
                interval={tickInterval}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: axisColor }}
                tickLine={false}
                axisLine={false}
                width={32}
              />
              {!isMobile && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: axisColor }}
                  tickLine={false}
                  axisLine={false}
                  width={32}
                  tickFormatter={(v) => `${v}`}
                />
              )}
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${gridColor}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value: any, name?: string | number) => {
                  if (name === 'bookingCount')
                    return [`${value} bookings`, 'Active bookings'];
                  if (name === 'checkIns') return [`${value}`, 'Check-ins'];
                  if (name === 'priorYearCount')
                    return [`${value} bookings`, 'Last year'];
                  return [value, name ?? ''];
                }}
                labelFormatter={(label) => label}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value) => {
                  if (value === 'bookingCount') return 'Active bookings';
                  if (value === 'checkIns') return 'Check-ins';
                  if (value === 'priorYearCount') return 'Last year';
                  return value;
                }}
              />
              {totalKennels > 0 && (
                <ReferenceLine
                  yAxisId="left"
                  y={totalKennels}
                  stroke={theme.palette.error.light}
                  strokeDasharray="5 3"
                  label={{
                    value: 'Capacity',
                    position: 'right',
                    fill: theme.palette.error.light,
                    fontSize: 11,
                  }}
                />
              )}
              {/* Prior year ghost area (YoY mode) */}
              {range === 'yoy' && (
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="priorYearCount"
                  fill="url(#priorGrad)"
                  stroke={axisColor}
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              )}
              {/* Main occupancy area */}
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="bookingCount"
                fill="url(#occupancyGrad)"
                stroke={primaryColor}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5, strokeWidth: 0 }}
              />
              {/* Check-in bars on right axis */}
              <Bar
                yAxisId={isMobile ? 'left' : 'right'}
                dataKey="checkIns"
                fill={secondaryColor}
                opacity={0.6}
                radius={[3, 3, 0, 0]}
                maxBarSize={isMobile ? 6 : 12}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Bottom row: radial gauge + monthly bars */}
      <Grid container spacing={3}>
        {/* Radial occupancy gauge */}
        <Grid item xs={12} sm={5} md={4}>
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Live occupancy
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Current kennel fill rate
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
              }}
            >
              <Box sx={{ position: 'relative', width: 200, height: 200 }}>
                <RadialBarChart
                  width={200}
                  height={200}
                  cx={100}
                  cy={100}
                  innerRadius={60}
                  outerRadius={90}
                  startAngle={210}
                  endAngle={-30}
                  data={[{ value: 100, fill: gridColor }, ...radialData]}
                >
                  <RadialBar
                    dataKey="value"
                    cornerRadius={8}
                    background={false}
                  />
                </RadialBarChart>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, lineHeight: 1 }}
                  >
                    {currentOccupancy}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    today
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                flexWrap: 'wrap',
                mt: 1,
              }}
            >
              <Chip
                size="small"
                label="Low < 50%"
                sx={{
                  backgroundColor: successColor,
                  color: '#fff',
                  fontSize: 11,
                }}
              />
              <Chip
                size="small"
                label="Busy 50–80%"
                sx={{
                  backgroundColor: theme.palette.warning.main,
                  color: '#fff',
                  fontSize: 11,
                }}
              />
              <Chip
                size="small"
                label="Full 80%+"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  color: '#fff',
                  fontSize: 11,
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Monthly check-in bars */}
        <Grid item xs={12} sm={7} md={8}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Monthly check-ins
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Number of arrivals per calendar month.
            </Typography>
            {monthlyData.length === 0 && !loading ? (
              <Typography color="textSecondary">No data available.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={monthlyData}
                  margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={gridColor}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: axisColor }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: axisColor }}
                    tickLine={false}
                    axisLine={false}
                    width={28}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${gridColor}`,
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v: any) =>
                      [`${v}`, 'Check-ins'] as [string, string]
                    }
                  />
                  <Bar
                    dataKey="checkIns"
                    fill={primaryColor}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BookingsAnalyticsPage;
